
import { computed, ComputedRef, defineComponent, nextTick, onMounted, PropType, ref, triggerRef, watch } from 'vue'
import InputKin from '@/views/components/InputKin.vue'
import { KeieiJokyoKmkTypeType, keieiJokyoKmkTypeType, optionalKeieiJokyoKmkTypeType, OptionalKeieiJokyoKmkTypeType } from '@/repositories/keiei-joho/data'
import LoadingButton from '@/views/components/LoadingButton.vue'
import { CorporationInfoPick, FacilityInfoPick, initializeKeieiJokyoKmk, KeieiJokyoKmkType, ResponseKeieiJokyoExportLayout } from '@/repositories/keiei-joho/keiei-jokyo/data'
import { hospitalClinicKbnType } from '@/views/corporation-info/types'

type CheckboxStateType = {
  isChecked: boolean,
  isDisabled: boolean
}
function initializeCheckboxState (item: KeieiJokyoKmkType): CheckboxStateType {
  const hasNonNullKin = item.kin !== null
  return {
    isChecked: hasNonNullKin,
    isDisabled: !hasNonNullKin
  }
}

export default defineComponent({
  components: { LoadingButton, InputKin },
  props: {
    corporationInfo: {
      type: Object as PropType<CorporationInfoPick>
    },
    facilityInfo: {
      type: Object as PropType<FacilityInfoPick>
    },
    keieiJokyoItems: {
      type: Array as PropType<KeieiJokyoKmkType[]>,
      required: true
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    }
  },
  emits: {
    register: () => true,
    recalculate: (callback: (data: ResponseKeieiJokyoExportLayout) => void) => { return typeof callback === 'function' }
  },
  setup (props, { emit }) {
    // 病院か診療所か
    const isHospitalKbn = props.facilityInfo?.hospitalClinicKbn === hospitalClinicKbnType.Hospital
    const isClinicKbn = props.facilityInfo?.hospitalClinicKbn === hospitalClinicKbnType.Clinic

    // kmkTypeごとにMapに変換（高速アクセス用）
    const keieiJokyoItemMap = computed(() => {
      return new Map(props.keieiJokyoItems.map(item => [item.kmkType, item]))
    })

    // Mapから項目を取得。存在しない場合はダミー項目を返す
    const getItem = (type: KeieiJokyoKmkTypeType): KeieiJokyoKmkType => {
      return keieiJokyoItemMap.value.get(type) ?? dummyItem
    }

    // ダミー項目（Mapに該当項目がない場合のフォールバック用）
    const dummyItem = initializeKeieiJokyoKmk()
    dummyItem.kmkType = keieiJokyoKmkTypeType.Dummy

    // 各kmkTypeごとのcomputedをまとめたマップ
    const kmkMap = {} as Record<KeieiJokyoKmkTypeType, ComputedRef<KeieiJokyoKmkType>>

    // 全てのkmkTypeに対してcomputedを生成
    for (const key of Object.values(keieiJokyoKmkTypeType)) {
      kmkMap[key] = computed(() => getItem(key))
    }

    // 任意科目用のチェックボックス
    const checkboxStates = ref<Record<OptionalKeieiJokyoKmkTypeType, CheckboxStateType>>({
      NyuinShuekiHoken: initializeCheckboxState(kmkMap.NyuinShuekiHoken.value),
      NyuinShuekiKogai: initializeCheckboxState(kmkMap.NyuinShuekiKogai.value),
      ShitsuryoShueki: initializeCheckboxState(kmkMap.ShitsuryoShueki.value),
      GairaiShuekiHoken: initializeCheckboxState(kmkMap.GairaiShuekiHoken.value),
      GairaiShuekiKogai: initializeCheckboxState(kmkMap.GairaiShuekiKogai.value),
      OtherShuekiHoken: initializeCheckboxState(kmkMap.OtherShuekiHoken.value),
      KyushokuItakuhi: initializeCheckboxState(kmkMap.KyushokuItakuhi.value),
      HonbuhiHaifu: initializeCheckboxState(kmkMap.HonbuhiHaifu.value),
      IgyogaiShuekiUketori: initializeCheckboxState(kmkMap.IgyogaiShuekiUketori.value),
      ShiharaiRisoku: initializeCheckboxState(kmkMap.ShiharaiRisoku.value),
      RinjiShueki: initializeCheckboxState(kmkMap.RinjiShueki.value),
      RinjiHiyo: initializeCheckboxState(kmkMap.RinjiHiyo.value),
      HojinJuminFutan: initializeCheckboxState(kmkMap.HojinJuminFutan.value)
    })

    // 科目のチェックボックスのありなしで、値を制御
    //   チェックあり：0を入れる
    //   チェックなし：nullを入れる。（見た目は空欄になる）。またdisabled状態にする
    const checkboxWatchers = () => {
      for (const key of Object.keys(checkboxStates.value) as OptionalKeieiJokyoKmkTypeType[]) {
        const checkboxState = checkboxStates.value[key]
        const itemRef = kmkMap[key]

        watch(() => checkboxState.isChecked, (checked) => {
          checkboxState.isDisabled = !checked
          itemRef.value.kin = checked ? 0 : null
        })
      }
    }

    // 循環参照を避けるために後ろの方でwatch式を作成
    checkboxWatchers()

    onMounted(async () => {
      // チェックボックス全てにtabindex="-1"を一括付与（テンプレート内で個別設定するのが冗長なため）
      document.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(el => {
        el.tabIndex = -1
      })

      // 自動計算処理を発火
      triggerRecalculation()
    })

    // 入院診療収益 その他の診療収益（入院診療収益 - 保険診療収益 - 公害等診療収益 - 室料差額収益）※室料差額収益は診療所の場合のみ
    watch(() => [kmkMap.NyuinShueki.value.kin, kmkMap.NyuinShuekiHoken.value.kin, kmkMap.NyuinShuekiKogai.value.kin, kmkMap.ShitsuryoShueki.value.kin], ([nyuin, hoken, kogai, shitsu]) => {
      const shitsuryo = isClinicKbn ? (shitsu) : 0
      kmkMap.NyuinShuekiOther.value.kin = (nyuin ?? 0) - (hoken ?? 0) - (kogai ?? 0) - (shitsuryo ?? 0)
    })

    // 外来診療収益 その他の診療収益（外来診療収益 - 入院診療収益 - 公害等診療収益）
    watch(() => [kmkMap.GairaiShueki.value.kin, kmkMap.GairaiShuekiHoken.value.kin, kmkMap.GairaiShuekiKogai.value.kin], ([gairai, hoken, kogai]) => {
      kmkMap.GairaiShuekiOther.value.kin = (gairai ?? 0) - (hoken ?? 0) - (kogai ?? 0)
    })

    // その他の医業収益（医業収益 - 入院診療収益 - 室料差額収益 - 外来診療収益）
    watch(() => [kmkMap.IgyoShueki.value.kin, kmkMap.NyuinShueki.value.kin, kmkMap.ShitsuryoShueki.value.kin, kmkMap.GairaiShueki.value.kin], ([igyo, nyuin, shitsu, gairai]) => {
      kmkMap.OtherShueki.value.kin = (igyo ?? 0) - (nyuin ?? 0) - (shitsu ?? 0) - (gairai ?? 0)
    })

    // 材料費（医薬品費 - 診療材料費 - 給食用材料費）
    watch(() => [kmkMap.Iyakuhinhi.value.kin, kmkMap.ShinryoZairyohi.value.kin, kmkMap.KyushokuZairyohi.value.kin], ([iyaku, shinryo, kyushoku]) => {
      kmkMap.Zairyohi.value.kin = (iyaku ?? 0) + (shinryo ?? 0) + (kyushoku ?? 0)
    })

    // 給与費（役員報酬 + 給料 + 賞与 + 賞与引当金繰入額 + 退職給付費用 + 法定福利費）
    watch(
      () => [kmkMap.YakuinHoshu.value.kin, kmkMap.Kyuryo.value.kin, kmkMap.Shoyo.value.kin, kmkMap.ShoyoHikiatekin.value.kin, kmkMap.TaishokuKyuhuHiyo.value.kin, kmkMap.HoteiFukurihi.value.kin],
      ([yaku, kyuryo, shoyo, shoyoHi, taishoku, hotei]) => {
        kmkMap.Kyuyohi.value.kin = (yaku ?? 0) + (kyuryo ?? 0) + (shoyo ?? 0) + (shoyoHi ?? 0) + (taishoku ?? 0) + (hotei ?? 0)
      }
    )

    // その他の医業費用（医業費用 - 材料費 - 給与費 - 委託費 - 減価償却費 - 器機賃借料）
    watch(
      () => [kmkMap.IgyoHiyo.value.kin, kmkMap.Zairyohi.value.kin, kmkMap.Kyuyohi.value.kin, kmkMap.Itakuhi.value.kin, kmkMap.GenkaShokyakuhi.value.kin, kmkMap.KikiChinshakuryo.value.kin],
      ([igyo, zairyo, kyuyo, itaku, genka, kiki]) => {
        if (isClinicKbn) {
          kmkMap.OtherIgyoHiyo.value.kin = (igyo ?? 0) - (zairyo ?? 0) - (kyuyo ?? 0) - (itaku ?? 0) - (genka ?? 0) - (kiki ?? 0)
        }
      }
    )

    // 医業利益（医業収益 - 医業費用）
    watch(() => [kmkMap.IgyoShueki.value.kin, kmkMap.IgyoHiyo.value.kin], ([shueki, hiyo]) => {
      kmkMap.IgyoRieki.value.kin = (shueki ?? 0) - (hiyo ?? 0)
    })

    // 経常利益（医業利益 + 医業外収益 - 医業外費用）
    watch(() => [kmkMap.IgyoRieki.value.kin, kmkMap.IgyogaiShueki.value.kin, kmkMap.IgyogaiHiyo.value.kin], ([rieki, shueki, hiyo]) => {
      kmkMap.KeijoRieki.value.kin = (rieki ?? 0) + (shueki ?? 0) - (hiyo ?? 0)
    })

    // 税引前当期純利益（経常利益 + 臨時収益 - 臨時費用）
    watch(() => [kmkMap.KeijoRieki.value.kin, kmkMap.RinjiShueki.value.kin, kmkMap.RinjiHiyo.value.kin], ([rieki, shueki, hiyo]) => {
      kmkMap.ZeibikimaeToukiJunrieki.value.kin = (rieki ?? 0) + (shueki ?? 0) - (hiyo ?? 0)
    })

    // 当期純利益（経常利益 - 法人税、住民税及び事業税負担額）
    watch(() => [kmkMap.ZeibikimaeToukiJunrieki.value.kin, kmkMap.HojinJuminFutan.value.kin], ([rieki, zei]) => {
      kmkMap.ToukiJunrieki.value.kin = (rieki ?? 0) - (zei ?? 0)
    })

    /**
     * 登録
     */
    const onClickedRegister = () => {
      updateNullToZero()
      emit('register')
    }

    /**
     * 再集計
     */
    const onClickedRecalculate = () => {
      emit('recalculate', async (data: ResponseKeieiJokyoExportLayout) => {
        await updateKeieiJokyoItem(data.kmkTypes)
      })
    }

    /**
     * 再集計による表示内容更新
     */
    const updateKeieiJokyoItem = async (itemList: KeieiJokyoKmkType[]) => {
      // チェックボックスがある科目に金額が入力されている場合、チェックを付けておく
      const optionalKmkTypes = optionalKeieiJokyoKmkTypeType as readonly string[]
      itemList.forEach(item => {
        const isOptionalKmk = optionalKmkTypes.includes(item.kmkType)
        const isInputted = item.kin !== null
        if (isOptionalKmk && isInputted) {
          checkboxStates.value[item.kmkType as OptionalKeieiJokyoKmkTypeType].isChecked = true
        }
      })

      // watchが確定してから金額項目を更新
      await nextTick()
      itemList.forEach(item => {
        const itemRef = kmkMap[item.kmkType]
        if (itemRef.value) {
          itemRef.value.kin = item.kin
        }
      })

      // 自動計算処理を発火
      triggerRecalculation()
    }

    /**
     * null項目を0に変換する（最小値が1の項目もあるが、そこまでは考慮しない）
     */
    const updateNullToZero = () => {
      props.keieiJokyoItems.forEach(item => {
        const itemRef = kmkMap[item.kmkType]

        if (itemRef.value.kin === null) {
          // 診療所区分の場合、室料差額収益・給食委託費・本部費配賦額・臨時収益・臨時費用は任意科目扱い。（つまり病院区分の時は0に更新すればいい）
          if (isHospitalKbn && (itemRef.value.kmkType === keieiJokyoKmkTypeType.ShitsuryoShueki ||
                                itemRef.value.kmkType === keieiJokyoKmkTypeType.KyushokuItakuhi ||
                                itemRef.value.kmkType === keieiJokyoKmkTypeType.RinjiShueki ||
                                itemRef.value.kmkType === keieiJokyoKmkTypeType.HonbuhiHaifu ||
                                itemRef.value.kmkType === keieiJokyoKmkTypeType.RinjiHiyo)
          ) {
            itemRef.value.kin = 0
          }
          // 任意科目以外を0に更新
          if (!(optionalKeieiJokyoKmkTypeType as readonly string[]).includes(itemRef.value.kmkType)) {
            itemRef.value.kin = 0
          }
        }
      })
    }

    /**
     * 意図的に自動計算を走らせる
     */
    const triggerRecalculation = () => {
      // 以下の５科目分、計算させれば、連動して全て計算される
      triggerRef(kmkMap.IgyoShueki)
      triggerRef(kmkMap.NyuinShueki)
      triggerRef(kmkMap.GairaiShueki)
      triggerRef(kmkMap.Iyakuhinhi)
      triggerRef(kmkMap.YakuinHoshu)
    }

    /**
     * 「病院」か「診療所」の文字列を返す
     */
    const clinicOrHospitalLabel = computed(() => {
      return props.facilityInfo?.hospitalClinicKbn === hospitalClinicKbnType.Hospital ? '病院' : '診療所'
    })

    return {
      isHospitalKbn,
      isClinicKbn,
      kmkMap,
      checkboxStates,

      onClickedRegister,
      onClickedRecalculate,
      clinicOrHospitalLabel
    }
  }
})
